import { useCallback, useMemo } from 'react';

import config from 'config';

import { useCustomer } from '@techstyle/react-accounts';
import { defineMessages, useIntl } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { useLDFlags } from './LD/useLDFlags';

const { getStartedMessage } = defineMessages({
  getStartedMessage: {
    id: 'global_cta.cognigy_get_started',
    defaultMessage: 'Get Started',
  },
});

export const WEB_CHAT_WINDOW_LOCATION = 'cognigyWebChat';
export const WEB_CHAT_LOCAL_STORAGE_KEYS = {
  lastActive: 'cognigyWebChatLastActive',
  lastClosed: 'cognigyWebChatLastClosed',
  sessionId: 'cognigyWebChatSessionId',
};
// TODO: This expiration time will be updated before release
const WEB_CHAT_SESSION_TIMEOUT = 1000 * 60 * 10; // 10 minutes

const cognigyRegionConfigs = config.get('public.cognigyWebChat.regionConfigs');

const useCognigyWebChatAction = () => {
  const { 'cognigy-widget': cognigyEnabled } = useLDFlags();
  const intl = useIntl();
  const { tld } = useDomain();
  const cognigyConfig = useMemo(() => cognigyRegionConfigs[tld], [tld]);

  const customer = useCustomer();

  const getShouldResumeChat = useCallback(() => {
    const lastActive = localStorage.getItem(
      WEB_CHAT_LOCAL_STORAGE_KEYS.lastActive
    );
    const lastClosed = localStorage.getItem(
      WEB_CHAT_LOCAL_STORAGE_KEYS.lastClosed
    );
    const sessionId = localStorage.getItem(
      WEB_CHAT_LOCAL_STORAGE_KEYS.sessionId
    );

    if (!lastActive || !sessionId) {
      return false;
    }

    const lastActiveTime = new Date(Number(lastActive)).getTime();
    const lastClosedTime = lastClosed
      ? new Date(Number(lastClosed)).getTime()
      : 0;

    if (lastActiveTime < lastClosedTime) {
      return false;
    }

    const currentTime = new Date().getTime();
    const sessionTimeout = lastActiveTime + WEB_CHAT_SESSION_TIMEOUT;

    return currentTime < sessionTimeout;
  }, []);

  const getIsChatSessionActive = useCallback(() => {
    const lastActive = localStorage.getItem(
      WEB_CHAT_LOCAL_STORAGE_KEYS.lastActive
    );
    const currentTime = new Date().getTime();
    const sessionTimeout =
      new Date(Number(lastActive)).getTime() + WEB_CHAT_SESSION_TIMEOUT;

    return currentTime < sessionTimeout;
  }, []);

  const initializeWebChat = useCallback(async () => {
    const cognigyWebChat = window[WEB_CHAT_WINDOW_LOCATION];
    if (!cognigyWebChat && window.initWebchat) {
      let cognigySessionId = localStorage.getItem(
        WEB_CHAT_LOCAL_STORAGE_KEYS.sessionId
      );
      const hasActiveSession = getIsChatSessionActive();

      // If there is no session ID or the session is not active, create a new session ID
      if (!cognigySessionId || !hasActiveSession) {
        cognigySessionId = `session-${Date.now() * Math.random()}`;
      }

      const additionalInitData = {};

      if (customer.id) {
        additionalInitData.userId = customer.id;
      }

      await window
        .initWebchat(
          `https://endpoint-app-us.cognigy.ai/${cognigyConfig.key}`,
          {
            sessionId: cognigySessionId,
            settings: {
              startBehavior: {
                startBehavior: 'injection',
                getStartedText: intl.formatMessage(getStartedMessage),
                getStartedData: {
                  customerId: customer.id ?? '',
                  name: `${customer.firstName ?? ''} ${
                    customer.lastName ?? ''
                  }`.trim(),
                  buCode: 'SX',
                  cogRegion: cognigyConfig.region,
                },
              },
            },
          }
        )
        .then(webChat => {
          localStorage.setItem(
            WEB_CHAT_LOCAL_STORAGE_KEYS.sessionId,
            cognigySessionId
          );
          window[WEB_CHAT_WINDOW_LOCATION] = webChat;

          window[WEB_CHAT_WINDOW_LOCATION].registerAnalyticsService(event => {
            if (event.type === 'webchat/close') {
              localStorage.setItem(
                WEB_CHAT_LOCAL_STORAGE_KEYS.lastClosed,
                new Date().getTime()
              );
            }

            if (event.type === 'webchat/open') {
              if (getIsChatSessionActive()) {
                localStorage.setItem(
                  WEB_CHAT_LOCAL_STORAGE_KEYS.lastActive,
                  new Date().getTime()
                );
                webChat.startConversation();
              }
            }

            if (
              ['webchat/incoming-message', 'webchat/outgoing-message'].includes(
                event.type
              )
            ) {
              localStorage.setItem(
                WEB_CHAT_LOCAL_STORAGE_KEYS.lastActive,
                new Date().getTime()
              );
            }
          });
        });
    }
  }, [cognigyConfig, customer, getIsChatSessionActive, intl]);

  const openWebChat = useCallback(() => {
    const hasActiveSession = getIsChatSessionActive();
    const webChat = window[WEB_CHAT_WINDOW_LOCATION];
    if (webChat) {
      webChat.open();

      if (hasActiveSession) {
        webChat.startConversation();
      }
    }
  }, [getIsChatSessionActive]);

  const cognigyHandler = useCallback(async () => {
    if (cognigyEnabled) {
      await initializeWebChat();
      openWebChat();
    }
  }, [cognigyEnabled, initializeWebChat, openWebChat]);

  return {
    cognigyHandler,
    cognigyEnabled,
    getShouldResumeChat,
  };
};

export default useCognigyWebChatAction;
